import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  createLeadPromise,
  updateLeadPromise,
  importLeadsFromCSV,
  importLeadsFromLinkedInSearch,
  getLinkedInAccounts,
} from "../../services/apiService";
import { useModal } from "../modal/ModalContext";
import FormContainer from "../../containers/FormContainer";
import TextBox from "../shared/TextBox";
import TextArea from "../shared/TextArea";
import TagInput from "../shared/TagInput";
import SelectBox from "../shared/SelectBox";
import SubmitButton from "../shared/SubmitButton";
import { getErrorMessage, notifyError } from "../../helpers/apiError";

const LeadModal = ({ listId, lead, onSuccess }) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();
  const isEditMode = !!lead;

  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  // Form state for manual add/edit
  const [form, setForm] = useState({
    firstName: lead?.firstName || "",
    lastName: lead?.lastName || "",
    email: lead?.email || "",
    phone: lead?.phone || "",
    company: lead?.company || "",
    jobTitle: lead?.jobTitle || "",
    location: lead?.location || "",
    industry: lead?.industry || "",
    linkedinUrl: lead?.linkedinUrl || "",
    notes: lead?.notes || "",
    campaignId: lead?.campaignId || "",
    linkedinAccountId: lead?.linkedinAccountId || "",
  });
  const [tags, setTags] = useState(
    lead?.tags?.map((tag) => ({ id: tag, text: tag })) || []
  );

  // CSV import state
  const [csvFile, setCsvFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // LinkedIn search import state
  const [searchUrl, setSearchUrl] = useState("");
  const [searchAccountId, setSearchAccountId] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [linkedinAccounts, setLinkedinAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // Fetch connected LinkedIn accounts when search tab is opened
  useEffect(() => {
    if (activeTab === 1 && linkedinAccounts.length === 0) {
      setLoadingAccounts(true);
      getLinkedInAccounts({ isConnected: true, limit: 100 })
        .then((res) => {
          const data = res.data;
          const list = Array.isArray(data) ? data : (data?.data || []);
          setLinkedinAccounts(list);
        })
        .catch(() => {})
        .finally(() => setLoadingAccounts(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const isFormDisabled = !form.firstName || !form.lastName || !form.email;

  const handleSubmit = async () => {
    setIsLoading(true);
    setFormError(null);

    const payload = {
      ...form,
      tags: tags.map((tag) => tag.text),
    };

    // Remove empty fields
    Object.keys(payload).forEach((key) => {
      if (payload[key] === "" || payload[key] === null || payload[key] === undefined) {
        delete payload[key];
      }
    });

    try {
      if (isEditMode) {
        await updateLeadPromise(listId, lead.id, payload);
        toast.success(t("leads.toast.updateSuccess"));
      } else {
        await createLeadPromise(listId, payload);
        toast.success(t("leads.toast.createSuccess"));
      }
      if (onSuccess) onSuccess();
      hideModal();
    } catch (err) {
      const msg = getErrorMessage(err, t("leads.toast.error"));
      setFormError(Array.isArray(msg) ? msg : msg);
      notifyError(err, t("leads.toast.error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCSVUpload = async () => {
    if (!csvFile) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      await importLeadsFromCSV(listId, formData);
      toast.success(t("leads.import.csvSuccess"));
      if (onSuccess) onSuccess();
      hideModal();
    } catch (err) {
      notifyError(err, t("leads.toast.error"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleLinkedInSearch = async () => {
    if (!searchUrl.trim() || !searchAccountId) return;
    setIsSearching(true);
    try {
      const response = await importLeadsFromLinkedInSearch(listId, {
        searchUrl,
        accountId: searchAccountId,
        limit: 1000,
      });
      toast.success(t("leads.import.linkedinSuccess"));
      if (onSuccess) onSuccess(response?.data);
      hideModal();
    } catch (err) {
      const msg = getErrorMessage(err, t("leads.toast.error"));
      notifyError(err, msg);
    } finally {
      setIsSearching(false);
    }
  };

  const tabs = [
    { label: t("leads.tabs.add"), id: 0 },
    { label: t("leads.tabs.search"), id: 1 },
    { label: t("leads.tabs.upload"), id: 2 },
  ];

  return (
    <div className="p-[30px] max-h-[80vh] overflow-y-auto">
      {/* Tabs (only in add mode) */}
      {!isEditMode && (
        <div className="flex mb-6 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2 text-sm font-semibold transition-colors ${
                activeTab === tab.id
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Tab 0: Add / Edit Lead form */}
      {(isEditMode || activeTab === 0) && (
        <FormContainer
          isLoading={isLoading}
          error={typeof formError === "string" || Array.isArray(formError) ? formError : null}
          onSubmit={handleSubmit}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            <TextBox
              label={t("leads.firstName") + " *"}
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              error={formError?.firstName}
            />
            <TextBox
              label={t("leads.lastName") + " *"}
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              error={formError?.lastName}
            />
            <TextBox
              label={t("leads.email") + " *"}
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              error={formError?.email}
            />
            <TextBox
              label={t("leads.phone")}
              name="phone"
              value={form.phone}
              onChange={handleChange}
            />
            <TextBox
              label={t("leads.company")}
              name="company"
              value={form.company}
              onChange={handleChange}
            />
            <TextBox
              label={t("leads.jobTitle")}
              name="jobTitle"
              value={form.jobTitle}
              onChange={handleChange}
            />
            <TextBox
              label={t("leads.location")}
              name="location"
              value={form.location}
              onChange={handleChange}
            />
            <TextBox
              label={t("leads.industry")}
              name="industry"
              value={form.industry}
              onChange={handleChange}
            />
            <TextBox
              label={t("leads.linkedinUrl")}
              name="linkedinUrl"
              value={form.linkedinUrl}
              onChange={handleChange}
              error={formError?.linkedinUrl}
            />
            <TextBox
              label="Campaign ID"
              name="campaignId"
              value={form.campaignId}
              onChange={handleChange}
            />
            <TextBox
              label="LinkedIn Account ID"
              name="linkedinAccountId"
              value={form.linkedinAccountId}
              onChange={handleChange}
            />
          </div>

          <TagInput
            tags={tags}
            handleAddition={(tag) => setTags([...tags, tag])}
            handleDelete={(i) => setTags(tags.filter((_, idx) => idx !== i))}
            label={t("leads.tags")}
          />

          <TextArea
            name="notes"
            value={form.notes}
            onChange={handleChange}
            label={t("leads.notes")}
            rows={3}
          />

          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              onClick={hideModal}
              className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t("form.cancel")}
            </button>
            <SubmitButton
              customStyle="inline"
              label={isEditMode ? t("form.save") : t("form.add")}
              disabled={isFormDisabled}
              isLoading={isLoading}
            />
          </div>
        </FormContainer>
      )}

      {/* Tab 1: LinkedIn Search Import */}
      {!isEditMode && activeTab === 1 && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-600">{t("leads.import.linkedinTitle")}</p>
          <SelectBox
            label={t("leads.import.selectAccount")}
            name="searchAccountId"
            value={searchAccountId}
            onChange={(e) => setSearchAccountId(e.target.value)}
            options={linkedinAccounts.map((acc) => ({
              value: acc.id,
              label: acc.accountName || acc.email || acc.id,
            }))}
            placeholder={loadingAccounts ? t("form.loading") : t("leads.import.selectAccount_placeholder")}
            disabled={loadingAccounts}
          />
          <TextBox
            label={t("leads.import.searchUrl")}
            name="searchUrl"
            value={searchUrl}
            onChange={(e) => setSearchUrl(e.target.value)}
            placeholder={t("leads.import.searchUrl_placeholder")}
          />
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={hideModal}
              className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t("form.cancel")}
            </button>
            <button
              type="button"
              onClick={handleLinkedInSearch}
              disabled={!searchUrl.trim() || !searchAccountId || isSearching}
              className="px-5 py-2 text-sm font-bold text-white rounded-md bg-blue-600 hover:opacity-90 disabled:opacity-50 disabled:pointer-events-none flex items-center gap-2"
            >
              {isSearching && (
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              )}
              {t("form.search")}
            </button>
          </div>
        </div>
      )}

      {/* Tab 2: CSV / XLSX Upload */}
      {!isEditMode && activeTab === 2 && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-600">{t("leads.import.csvTitle")}</p>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setCsvFile(e.target.files[0])}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-600 file:text-white
                hover:file:opacity-90 cursor-pointer"
            />
            {csvFile && (
              <p className="mt-2 text-sm text-gray-600">{csvFile.name}</p>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={hideModal}
              className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t("form.cancel")}
            </button>
            <button
              type="button"
              onClick={handleCSVUpload}
              disabled={!csvFile || isUploading}
              className="px-5 py-2 text-sm font-bold text-white rounded-md bg-blue-600 hover:opacity-90 disabled:opacity-50 disabled:pointer-events-none flex items-center gap-2"
            >
              {isUploading && (
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              )}
              {t("form.upload")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadModal;
